import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import DigitalCardFilter from '../DigitalCard/DigitalCardFilter';
import './DiscoverWithFilter.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faHeart,
  faUserCircle,
  faShoppingBag,
  faPaperPlane,
  faBookmark,
  faFilter,
  faSearch,
  faBars,
  faTimes,
  faArrowUp,
  faArrowDown,
  faArrowsUpDown
} from '@fortawesome/free-solid-svg-icons';

// Firebase imports
import { db } from '../../services/firebase';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';

/**
 * DiscoverWithFilter Component with Shop-style sidebars and video backgrounds
 * + Mobile CLEANED: Removed duplicate navigation, using global App.js navigation
 */
const DiscoverWithFilter = () => {
  // Router hooks for URL parameter handling
  const location = useLocation();
  const navigate = useNavigate();
  
  // Access user context for Digital Card functionality
  const { 
    user, 
    savedItems, 
    saveItem, 
    dismissItem, 
    isSaved,
    updateDigitalCardFromInteraction
  } = useContext(UserContext);

  const [allItems, setAllItems] = useState([]);
  const [displayItems, setDisplayItems] = useState([]);
  const [visibleItems, setVisibleItems] = useState([]);
  const [suggestedAccounts, setSuggestedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('global'); // Default to global view
  const [hoverStates, setHoverStates] = useState({});
  const [failedImages, setFailedImages] = useState({});
  const [screenSize, setScreenSize] = useState('large'); // large, medium, small
  const [searchQuery, setSearchQuery] = useState(''); // Add search query state
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [scrollDirection, setScrollDirection] = useState('up'); // Track scroll direction
  const [currentScrollY, setCurrentScrollY] = useState(0); // Renamed to avoid conflict
  const [showBottomNav, setShowBottomNav] = useState(false); // Show bottom navigation on scroll up
  
  // NEW MOBILE STATES - From Figma specifications
  const [selectedCategory, setSelectedCategory] = useState('Profiles');
  const [mobileVideoInteractionStarted, setMobileVideoInteractionStarted] = useState(false);
  const [sortOption, setSortOption] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);
  
  const itemsPerPage = 24; // Number of items to load at once
  const observer = useRef();
  const lastScrollY = useRef(0);
  
  // Video references for boomerang effect (Shop-style)
  const leftVideoRef = useRef(null);
  const rightVideoRef = useRef(null);
  
  // Flag to prevent duplicate filter operations
  const filterOperationInProgress = useRef(false);
  const prevScrollY = useRef(0);
  const loadingMoreRef = useRef(false);
  
  // Debug mode for troubleshooting
  const debug = true; // Enable to see Firebase loading messages

  // NEW: Category options from Figma design
  const categoryOptions = [
    'Profiles',
    'Architecture', 
    'Art', 
    'Design', 
    'Interiors', 
    'Travel'
  ];

  // Window resize handler to fix navigation overlap issue
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Initial check
    handleResize();

    // Add resize listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Mobile video interaction handler - Progressive enhancement approach
  useEffect(() => {
    if (windowWidth >= 1300) {
      // Desktop: Videos autoplay immediately, no interaction needed
      setMobileVideoInteractionStarted(true);
      return;
    }

    // Mobile: Wait for user interaction before starting videos
    if (mobileVideoInteractionStarted) return;

    console.log('📱 Mobile detected: Waiting for user interaction to start videos');

    const handleInteraction = () => {
      console.log('📱 User interaction detected: Starting mobile videos');
      setMobileVideoInteractionStarted(true);
      
      // Start videos after interaction
      if (leftVideoRef.current) {
        leftVideoRef.current.play().catch(console.error);
      }
      if (rightVideoRef.current) {
        rightVideoRef.current.play().catch(console.error);
      }
      
      // Remove event listeners after first interaction
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('click', handleInteraction);
    };

    // Listen for various user interactions
    window.addEventListener('touchstart', handleInteraction, { passive: true });
    window.addEventListener('scroll', handleInteraction, { passive: true });
    window.addEventListener('click', handleInteraction, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('click', handleInteraction);
    };
  }, [windowWidth, mobileVideoInteractionStarted]);

  // Boomerang video effect setup (copied from Shop)
  const setupBoomerangEffect = useCallback((videoElement) => {
    if (!videoElement) return;
    
    let isReversing = false;
    let cleanupFunctions = [];
    
    const handleTimeUpdate = () => {
      if (isReversing) {
        if (videoElement.currentTime <= 0.1) {
          // Reached beginning, start forward
          isReversing = false;
          videoElement.currentTime = 0;
          videoElement.play().catch(console.error);
        }
      }
    };
    
    const handleEnded = () => {
      if (!isReversing) {
        // Reached end, start reverse
        isReversing = true;
        videoElement.currentTime = videoElement.duration - 0.1;
        
        // Manual reverse playback
        const reverseInterval = setInterval(() => {
          if (videoElement.currentTime <= 0.1) {
            clearInterval(reverseInterval);
            isReversing = false;
            videoElement.currentTime = 0;
            videoElement.play().catch(console.error);
          } else {
            videoElement.currentTime -= 0.033; // ~30fps reverse
          }
        }, 33);
        
        cleanupFunctions.push(() => clearInterval(reverseInterval));
      }
    };
    
    const handleCanPlayThrough = () => {
      // Only auto-start on desktop or after mobile interaction
      if (windowWidth >= 1300 || mobileVideoInteractionStarted) {
        videoElement.play().catch(console.error);
      }
    };
    
    const handleLoadedData = () => {
      // Set slower playback speed
      videoElement.playbackRate = 0.6; // 60% speed
      // Optimize video quality settings
      videoElement.style.imageRendering = 'auto'; // Let browser choose best quality
      videoElement.style.filter = 'contrast(1.05) saturate(1.05) brightness(1.02)'; // Subtle enhancement
      
      // Only auto-start on desktop or after mobile interaction
      if (windowWidth >= 1300 || mobileVideoInteractionStarted) {
        videoElement.play().catch(console.error);
      }
    };
    
    // Add all event listeners
    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('ended', handleEnded);
    videoElement.addEventListener('canplaythrough', handleCanPlayThrough);
    videoElement.addEventListener('loadeddata', handleLoadedData);
    
    // Try to start playing immediately
    if (videoElement.readyState >= 3) { // HAVE_FUTURE_DATA
      handleLoadedData();
    }
    
    // Cleanup function
    const cleanup = () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('ended', handleEnded);
      videoElement.removeEventListener('canplaythrough', handleCanPlayThrough);
      videoElement.removeEventListener('loadeddata', handleLoadedData);
      cleanupFunctions.forEach(fn => fn());
    };
    
    cleanupFunctions.push(cleanup);
    return cleanup;
  }, [windowWidth, mobileVideoInteractionStarted]);

  // Setup videos when refs are available - cleanup on unmount
  useEffect(() => {
    // Cleanup any existing video effects when component unmounts
    return () => {
      if (leftVideoRef.current) {
        leftVideoRef.current.pause();
      }
      if (rightVideoRef.current) {
        rightVideoRef.current.pause();
      }
    };
  }, []);

  // Toggle filters visibility
  const toggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  // NEW: Handle category selection
  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category);
    if (category === 'Profiles') {
      setSearchQuery('');
    } else {
      setSearchQuery(category.toLowerCase());
    }
  }, []);

  // Get search query from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('q');
    const sourceParam = urlParams.get('source');

    if (searchParam && searchParam !== searchQuery) {
      setSearchQuery(searchParam);
      // Update selected category based on search
      const matchingCategory = categoryOptions.find(cat =>
        cat.toLowerCase() === searchParam.toLowerCase()
      );
      if (matchingCategory) {
        setSelectedCategory(matchingCategory);
      }
    } else if (sourceParam) {
      // Filter by source
      setSearchQuery(sourceParam);
    }
  }, [location.search, searchQuery, categoryOptions]);

  // Listen for header search events
  useEffect(() => {
    const handleHeaderSearch = (event) => {
      const searchTerm = event.detail?.searchTerm || '';
      setSearchQuery(searchTerm);
      
      // Update URL parameter
      const newUrl = new URL(window.location);
      if (searchTerm.trim()) {
        newUrl.searchParams.set('q', searchTerm);
      } else {
        newUrl.searchParams.delete('q');
      }
      navigate(newUrl.pathname + newUrl.search, { replace: true });
    };

    window.addEventListener('artifex-search', handleHeaderSearch);
    
    return () => {
      window.removeEventListener('artifex-search', handleHeaderSearch);
    };
  }, [navigate]);

  // Filter content based on search query
  const filterContentBySearch = useCallback((items) => {
    if (!searchQuery.trim()) {
      return items;
    }

    const lowercaseQuery = searchQuery.toLowerCase();
    const urlParams = new URLSearchParams(window.location.search);
    const sourceParam = urlParams.get('source');

    return items.filter(item => {
      // If filtering by source, match exact source
      if (sourceParam) {
        return item.source === sourceParam;
      }

      // Otherwise do general search
      const title = (item.title || '').toLowerCase();
      const description = (item.description || '').toLowerCase();
      const source = (item.source || '').toLowerCase();
      const tags = (item.tags || []).join(' ').toLowerCase();

      return (
        title.includes(lowercaseQuery) ||
        description.includes(lowercaseQuery) ||
        source.includes(lowercaseQuery) ||
        tags.includes(lowercaseQuery)
      );
    });
  }, [searchQuery]);

  // Handle responsive screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 600) {
        setScreenSize('small');
      } else if (width < 1000) {
        setScreenSize('medium');
      } else {
        setScreenSize('large');
      }
    };

    // Initial check
    handleResize();

    // Add resize listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle scroll direction for category menu visibility and bottom nav
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;

      if (scrollY > currentScrollY) {
        setScrollDirection('down');
        setShowBottomNav(false); // Hide bottom nav when scrolling down
      } else {
        setScrollDirection('up');
        setShowBottomNav(scrollY > 100); // Show bottom nav when scrolling up (after 100px)
      }

      setCurrentScrollY(scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [currentScrollY]);

  // Handle scroll for hiding/showing header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Hide header when scrolling down, show when scrolling up
      if (currentScrollY > lastScrollY.current + 10) {
        // setShowHeader(false); // Commented out since not used
      } else if (currentScrollY < lastScrollY.current - 10) {
        // setShowHeader(true); // Commented out since not used
      }
      
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle card hover state
  const handleCardMouseEnter = useCallback((itemId) => {
    setHoverStates(prev => ({
      ...prev,
      [itemId]: true
    }));
  }, []);

  const handleCardMouseLeave = useCallback((itemId) => {
    setHoverStates(prev => ({
      ...prev,
      [itemId]: false
    }));
  }, []);

  // Helper function to shuffle array
  const shuffleArray = useCallback((array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  // FIXED: Normalize image path - remove proxy for Met Museum images since they're failing
  const normalizeImagePath = useCallback((imagePath) => {
    if (!imagePath) return null;
    
    // Handle absolute URLs (starting with http/https)
    if (imagePath.startsWith('http')) {
      // FIXED: Don't use proxy for Met Museum images - try direct access first
      return imagePath;
    }
    
    // Handle relative paths
    const cleanPath = imagePath.replace(/^\/+/, '');
    return `/${cleanPath}`;
  }, []);

  // Handle image load failure
  const handleImageError = useCallback((itemId, imageUrl, originalUrl) => {
    if (debug) {
      console.log(`⚠️ Image failed to load: ${itemId}`, { imageUrl, originalUrl });
    }
    
    setFailedImages(prev => ({
      ...prev,
      [itemId]: true
    }));
  }, [debug]);

  // Preload next batch of images for smoother transitions
  const preloadNextBatch = useCallback((items, startIndex, count) => {
    const endIndex = Math.min(startIndex + count, items.length);
    
    for (let i = startIndex; i < endIndex; i++) {
      const item = items[i];
      if (item && item.image && !failedImages[item.id]) {
        const img = new Image();
        img.src = item.image;
      }
    }
  }, [failedImages]);

  // Add more items to visible set - defined as useCallback to fix ESLint warning
  const addMoreItemsFn = useCallback(() => {
    if (loadingMoreRef.current) return;
    if (visibleItems.length >= displayItems.length) {
      // If we've shown all items, start over from the beginning for a continuous loop
      if (displayItems.length > 0) {
        const nextBatch = displayItems.slice(0, itemsPerPage);
        setVisibleItems(prev => [...prev, ...nextBatch]);
        
        // Preload the batch after this one
        setTimeout(() => {
          preloadNextBatch(displayItems, 0, itemsPerPage * 2);
        }, 100);
      }
      return;
    }
    
    loadingMoreRef.current = true;
    
    setTimeout(() => {
      const currentCount = visibleItems.length;
      const nextBatchIndex = currentCount % displayItems.length;
      const remainingItems = displayItems.length - nextBatchIndex;
      const itemsToAdd = Math.min(itemsPerPage, remainingItems);
      
      const nextBatch = displayItems.slice(nextBatchIndex, nextBatchIndex + itemsToAdd);
      
      // If we're near the end of the array, also get items from the beginning
      let wrappedItems = [];
      if (itemsToAdd < itemsPerPage && displayItems.length > 0) {
        const neededFromStart = itemsPerPage - itemsToAdd;
        wrappedItems = displayItems.slice(0, neededFromStart);
      }
      
      setVisibleItems(prev => [...prev, ...nextBatch, ...wrappedItems]);
      
      // Preload the next batch for smoother scrolling
      const nextPreloadIndex = (nextBatchIndex + itemsToAdd) % displayItems.length;
      setTimeout(() => {
        preloadNextBatch(displayItems, nextPreloadIndex, itemsPerPage * 2);
      }, 100);
      
      loadingMoreRef.current = false;
    }, 100);
  }, [displayItems, visibleItems, itemsPerPage, preloadNextBatch]);

  // Use a simple state variable to track if we need to load more items
  const [shouldLoadMore, setShouldLoadMore] = useState(false);

  // Watch for changes to shouldLoadMore and call addMoreItemsFn when it changes to true
  useEffect(() => {
    if (shouldLoadMore) {
      addMoreItemsFn();
      setShouldLoadMore(false);
    }
  }, [shouldLoadMore, addMoreItemsFn]);

  // Infinite scroll implementation
  const lastItemRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && displayItems.length > visibleItems.length) {
        setShouldLoadMore(true); // Trigger load more through state change
      }
    });
    
    if (node) observer.current.observe(node);
  }, [displayItems, visibleItems]);

  // Add scroll handler for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Only handle downward scrolls
      if (currentScrollY <= prevScrollY.current) {
        prevScrollY.current = currentScrollY;
        return;
      }
      
      prevScrollY.current = currentScrollY;
      
      // Calculate position relative to bottom
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollBottom = documentHeight - (currentScrollY + windowHeight);
      
      // Load more when within 30% of viewport height from bottom
      const triggerDistance = windowHeight * 0.3;
      
      if (scrollBottom < triggerDistance && !loadingMoreRef.current) {
        setShouldLoadMore(true); // Trigger load more through state change
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // FIREBASE: Fetch content from Firebase instead of JSON file
  useEffect(() => {
    const fetchContentFromFirebase = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        if (debug) console.log("🔥 Fetching approved content from Firebase...");
        
        // Query Firebase for approved content
        const discoverCollection = collection(db, 'discover');
        const approvedQuery = query(
          discoverCollection,
          where('status', '==', 'approved'),
          orderBy('createdAt', 'desc'),
          limit(30) // Adjust limit as needed
        );
        
        // Skip Firebase - go straight to JSON fallback
        throw new Error('Using JSON fallback for faster mobile loading');
        
      } catch (err) {
        console.error('❌ Error loading content from Firebase:', err);
        console.log('🔄 Loading fallback data from JSON...');
        
        // Fallback to JSON file when Firebase fails/times out
        try {
          const response = await fetch('/discover.json');
          const jsonData = await response.json();
          
          if (debug) console.log(`📁 Loaded ${jsonData.entries?.length || jsonData.length} items from JSON fallback`);
          
          // Process JSON data to match expected format
          const processedJsonItems = (jsonData.entries || jsonData || []).map(item => ({
            ...item,
            image: normalizeImagePath(item.imageUrl || item.image_url),
            imageUrl: item.imageUrl || item.image_url
          }));

          setAllItems(shuffleArray(processedJsonItems));
          setError(null); // Clear error since fallback worked
          
        } catch (jsonError) {
          console.error('❌ Both Firebase and JSON failed:', jsonError);
          setError('Failed to load content from both Firebase and local data');
          setAllItems([]);
        }
        
        setIsLoading(false);
      }
    };

    fetchContentFromFirebase();
  }, [
    debug, normalizeImagePath, shuffleArray
  ]);

  // Update display items when view mode, search query, or allItems changes
  useEffect(() => {
    if (!allItems.length) return;
    
    // Skip if loading
    if (isLoading) return;
    
    let itemsToDisplay = [];
    
    if (viewMode === 'global') {
      // For global view, start with all items
      itemsToDisplay = allItems.filter(item => !failedImages[item.id]);
    } else if (viewMode === 'saved' && savedItems) {
      // For saved view, filter to only show saved items
      itemsToDisplay = allItems.filter(item => 
        savedItems.includes(item.id) && !failedImages[item.id]
      );
    } else if (viewMode === 'curated') {
      // For curated view, let the DigitalCardFilter component handle it
      return;
    }
    
    // Apply search filter
    let filteredItems = filterContentBySearch(itemsToDisplay);

    // Apply sorting
    if (sortOption === 'newest') {
      filteredItems = [...filteredItems].sort((a, b) => {
        const dateA = new Date(a.createdAt || a.timestamp || 0);
        const dateB = new Date(b.createdAt || b.timestamp || 0);
        return dateB - dateA; // Newest first
      });
    } else if (sortOption === 'popular') {
      filteredItems = [...filteredItems].sort((a, b) => {
        const popularityA = (a.likes || 0) + (a.views || 0) + (a.saves || 0);
        const popularityB = (b.likes || 0) + (b.views || 0) + (b.saves || 0);
        return popularityB - popularityA; // Most popular first
      });
    }
    // 'featured' stays in default order

    setDisplayItems(filteredItems);
    setVisibleItems(filteredItems.slice(0, itemsPerPage));
    
    // Preload the next batch of images
    setTimeout(() => {
      preloadNextBatch(filteredItems, itemsPerPage, itemsPerPage);
    }, 500);
  }, [
    viewMode, allItems, savedItems, failedImages, searchQuery,
    isLoading, itemsPerPage, preloadNextBatch, filterContentBySearch, sortOption
  ]);

  // Handle filtered content change from DigitalCardFilter
  const handleFilteredContentChange = useCallback((filteredItems) => {
    if (!filteredItems || filterOperationInProgress.current) return;
    
    filterOperationInProgress.current = true;
    
    try {
      // Remove failed images from the display items
      let validItems = filteredItems.filter(item => !failedImages[item.id]);
      
      // Apply search filter to curated content
      validItems = filterContentBySearch(validItems);
      
      // Only update if the items have actually changed
      if (JSON.stringify(validItems.map(item => item.id)) !== 
          JSON.stringify(displayItems.map(item => item.id))) {
        
        setDisplayItems(validItems);
        setVisibleItems(validItems.slice(0, itemsPerPage));
        
        // Preload the next batch of images
        setTimeout(() => {
          preloadNextBatch(validItems, itemsPerPage, itemsPerPage);
        }, 500);
      }
    } finally {
      // Always reset the flag when done
      filterOperationInProgress.current = false;
    }
  }, [displayItems, failedImages, itemsPerPage, preloadNextBatch, filterContentBySearch]);

  // Handle save/like action
  const handleSave = useCallback((e, item) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (saveItem) {
      saveItem(item.id);
      
      // Learn from this interaction
      if (updateDigitalCardFromInteraction) {
        updateDigitalCardFromInteraction(item, 'like');
      }
      
      if (debug) console.log('Saved item:', item.title || item.id);
    }
  }, [saveItem, updateDigitalCardFromInteraction, debug]);

  // Handle dismiss action
  const handleDismiss = useCallback((e, item) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (dismissItem) {
      dismissItem(item.id);
      
      // Learn from this interaction
      if (updateDigitalCardFromInteraction) {
        updateDigitalCardFromInteraction(item, 'dismiss');
      }
      
      if (debug) console.log('Dismissed item:', item.title || item.id);
    }
    
    // Immediately remove this item from display
    setDisplayItems(prev => prev.filter(i => i.id !== item.id));
    setVisibleItems(prev => prev.filter(i => i.id !== item.id));
  }, [dismissItem, updateDigitalCardFromInteraction, debug]);

  // Handle share action
  const handleShare = useCallback(async (e, itemId) => {
    e.preventDefault();
    e.stopPropagation();

    const url = `${window.location.origin}/discover/${itemId}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Check this out on Artifex',
          text: 'Found this on Artifex Discover',
          url: url
        });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
      }
    }
  }, []);
  
  // Handle view mode change
  const handleViewModeChange = useCallback((mode) => {
    if (mode === viewMode) return; // Skip if mode hasn't changed
    setViewMode(mode);
  }, [viewMode]);

  // Check if an item is saved
  const isItemSaved = useCallback((itemId) => {
    return isSaved ? isSaved(itemId) : savedItems?.includes(itemId) || false;
  }, [isSaved, savedItems]);

  if (isLoading && !visibleItems.length) {
    return (
      <div className="discover-loading">
        <div className="loading-spinner"></div>
        <p>Loading discover feed from Firebase...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="discover-error">
        <h2>Error loading content</h2>
        <p>{error}</p>
        <p>Make sure you have approved content in your Firebase 'discover' collection.</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <>
      {/* REMOVED: All duplicate mobile navigation elements */}
      {/* Navigation is now handled by global App.js hamburger menu */}

      {/* Desktop view mode buttons for wide screens only - COMMENTED OUT */}
      {/* 
      {windowWidth >= 1300 && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 200,
          display: 'flex',
          gap: '10px',
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '10px',
          borderRadius: '8px',
          backdropFilter: 'blur(10px)'
        }}>
          <button 
            onClick={() => handleViewModeChange('global')}
            style={{
              padding: '8px 12px',
              background: viewMode === 'global' ? '#0d47a1' : 'transparent',
              color: viewMode === 'global' ? 'white' : '#0d47a1',
              border: '1px solid #0d47a1',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            <FontAwesomeIcon icon={faHome} style={{ marginRight: '4px' }} />
            Global
          </button>
          
          <button 
            onClick={() => handleViewModeChange('curated')}
            disabled={!user || !user.curationSignature}
            style={{
              padding: '8px 12px',
              background: viewMode === 'curated' ? '#0d47a1' : 'transparent',
              color: viewMode === 'curated' ? 'white' : '#0d47a1',
              border: '1px solid #0d47a1',
              borderRadius: '4px',
              cursor: (!user || !user.curationSignature) ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              opacity: (!user || !user.curationSignature) ? 0.5 : 1
            }}
          >
            <FontAwesomeIcon icon={faUserCircle} style={{ marginRight: '4px' }} />
            Curated
          </button>
          
          <button 
            onClick={() => handleViewModeChange('saved')}
            disabled={!savedItems || savedItems.length === 0}
            style={{
              padding: '8px 12px',
              background: viewMode === 'saved' ? '#0d47a1' : 'transparent',
              color: viewMode === 'saved' ? 'white' : '#0d47a1',
              border: '1px solid #0d47a1',
              borderRadius: '4px',
              cursor: (!savedItems || savedItems.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              opacity: (!savedItems || savedItems.length === 0) ? 0.5 : 1
            }}
          >
            <FontAwesomeIcon icon={faHeart} style={{ marginRight: '4px' }} />
            Saved
          </button>
        </div>
      )}
      */}

      {/* Main content container */}
      <div className="discover-container">
        {/* Left sidebar with video background - SHOP STYLE */}
        {windowWidth >= 1300 && (
          <>
            {/* Left Sidebar Video Background */}
            <div className="discover-left-video-strip">
              <video
                ref={leftVideoRef}
                src="/videos/kohala-coast-arial.mov"
                muted
                loop={false}
                playsInline
                preload="metadata"
                autoPlay={windowWidth >= 1300}
                onLoadedData={() => {
                  if (leftVideoRef.current) {
                    setupBoomerangEffect(leftVideoRef.current);
                  }
                }}
                onError={(e) => {
                  console.error('Left video error:', e);
                }}
              />
            </div>
            
            {/* Left Sidebar Content */}
            <div className="discover-left-sidebar">
              <div className="sidebar-section" style={{
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                padding: '0.2rem 1rem 1rem',
                marginLeft: '10px',
                marginRight: '10px',
                marginTop: '10px',
                marginBottom: '20px',
                minHeight: '650px'
              }}>
                <h3>Browse Filters</h3>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '0.5rem',
                  maxHeight: '585px',
                  overflowY: 'auto',
                  marginTop: '0rem',
                  marginBottom: '1rem'
                }}>
                  <button 
                    onClick={() => setSearchQuery('architecture')}
                    className={searchQuery === 'architecture' ? 'selected' : ''}
                  >
                    Architecture
                  </button>
                  <button 
                    onClick={() => setSearchQuery('art')}
                    className={searchQuery === 'art' ? 'selected' : ''}
                  >
                    Art
                  </button>
                  <button 
                    onClick={() => setSearchQuery('design')}
                    className={searchQuery === 'design' ? 'selected' : ''}
                  >
                    Design
                  </button>
                  <button 
                    onClick={() => setSearchQuery('interiors')}
                    className={searchQuery === 'interiors' ? 'selected' : ''}
                  >
                    Interiors
                  </button>
                  <button 
                    onClick={() => setSearchQuery('nature')}
                    className={searchQuery === 'nature' ? 'selected' : ''}
                  >
                    Nature
                  </button>
                  <button 
                    onClick={() => setSearchQuery('travel')}
                    className={searchQuery === 'travel' ? 'selected' : ''}
                  >
                    Travel
                  </button>
                  <button 
                    onClick={() => setSearchQuery('photography')}
                    className={searchQuery === 'photography' ? 'selected' : ''}
                  >
                    Photography
                  </button>
                  <button 
                    onClick={() => setSearchQuery('ceramics')}
                    className={searchQuery === 'ceramics' ? 'selected' : ''}
                  >
                    Ceramics
                  </button>
                  <button 
                    onClick={() => setSearchQuery('fashion')}
                    className={searchQuery === 'fashion' ? 'selected' : ''}
                  >
                    Fashion
                  </button>
                  <button 
                    onClick={() => setSearchQuery('sculpture')}
                    className={searchQuery === 'sculpture' ? 'selected' : ''}
                  >
                    Sculpture
                  </button>
                </div>

                <div style={{
                  fontSize: '11px',
                  color: '#666',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  padding: '0.5rem',
                  marginTop: '0.5rem'
                }}>
                  <FontAwesomeIcon icon={faFilter} style={{ marginRight: '0.25rem' }} />
                  Quick filters
                </div>
              </div>
            </div>
          </>
        )}
        
        {/* Video sidebars for ALL screen sizes - ALWAYS VISIBLE */}
        <>
          <div className="discover-left-video-strip">
            <video
              ref={leftVideoRef}
              src="/videos/kohala-coast-arial.mov"
              muted
              loop={false}
              playsInline
              preload="metadata"
              autoPlay
              onLoadedData={() => {
                if (leftVideoRef.current) {
                  setupBoomerangEffect(leftVideoRef.current);
                }
              }}
              onError={(e) => {
                console.error('Left video error:', e);
              }}
            />
          </div>
          
          <div className="discover-right-video-strip">
            <video
              ref={rightVideoRef}
              src="/videos/kohala-coast-arial.mov"
              muted
              loop={false}
              playsInline
              preload="metadata"
              autoPlay={windowWidth >= 1300}
              onLoadedData={() => {
                if (rightVideoRef.current) {
                  setupBoomerangEffect(rightVideoRef.current);
                }
              }}
              onError={(e) => {
                console.error('Right video error:', e);
              }}
            />
          </div>
        </>
        
        {/* Fixed Bottom Navigation - Visible on scroll up */}
        <div style={{
          position: 'fixed',
          bottom: showBottomNav ? '20px' : '-80px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1rem',
          padding: '12px 24px',
          background: 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: '50px',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 2px 4px rgba(255, 255, 255, 0.5)',
          transition: 'bottom 0.3s ease',
          zIndex: 1000
        }}>
            <Link
                to="/saved"
                title="Saved items"
                style={{
                  padding: 0,
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(20px) saturate(200%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(200%)',
                  color: '#666',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -2px 4px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  if (windowWidth >= 576) {
                    e.currentTarget.style.backdropFilter = 'blur(20px) saturate(180%) brightness(1.1)';
                    e.currentTarget.style.WebkitBackdropFilter = 'blur(20px) saturate(180%) brightness(1.1)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.6), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backdropFilter = 'blur(16px) saturate(180%)';
                  e.currentTarget.style.WebkitBackdropFilter = 'blur(16px) saturate(180%)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
                }}
              >
                <FontAwesomeIcon icon={faHeart} style={{ fontSize: '16px' }} />
              </Link>

              <button
                  onClick={toggleFilters}
                  title="Filters"
                  style={{
                    padding: 0,
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(20px) saturate(200%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(200%)',
                    color: '#666',
                    border: '1px solid rgba(255, 255, 255, 0.5)',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    if (windowWidth >= 576) {
                      
                      
                      e.currentTarget.style.backdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.6), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    
                    
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.backdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
                  }}
                >
                  <FontAwesomeIcon icon={faFilter} style={{ fontSize: '16px' }} />
                </button>

              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  title={`Sort: ${sortOption === 'featured' ? 'Featured' : sortOption === 'newest' ? 'Newest' : 'Most Popular'}`}
                  style={{
                    padding: 0,
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(20px) saturate(200%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(200%)',
                    color: '#666',
                    border: '1px solid rgba(255, 255, 255, 0.5)',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 2px 4px rgba(255, 255, 255, 0.6), inset 0 -2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                  onClick={() => {
                    const select = document.getElementById('discover-sort-select');
                    if (select) select.focus();
                  }}
                  onMouseEnter={(e) => {
                    if (windowWidth >= 576) {
                      
                      
                      e.currentTarget.style.backdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(20px) saturate(180%) brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15), inset 0 1px 1px rgba(255, 255, 255, 0.6), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    
                    
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.backdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.WebkitBackdropFilter = 'blur(16px) saturate(180%)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5), inset 0 -1px 1px rgba(0, 0, 0, 0.05)';
                  }}
                >
                  <FontAwesomeIcon icon={faArrowsUpDown} style={{ fontSize: '16px' }} />
                </button>
                <select
                  id="discover-sort-select"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '40px',
                    height: '40px',
                    opacity: 0,
                    cursor: 'pointer',
                    borderRadius: '50%'
                  }}
                >
                  <option value="featured">Featured</option>
                  <option value="newest">Newest</option>
                  <option value="popular">Most Popular</option>
                </select>
              </div>
        </div>

        {/* Main grid container */}
        <div className={`discover-main ${windowWidth < 1300 ? 'mobile-layout' : ''}`}>
          {/* Search results indicator */}
          {searchQuery && (
            <div style={{
              padding: '1rem',
              marginBottom: '1rem',
              background: '#f8f9fa',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <p style={{
                margin: 0,
                fontSize: '0.9rem',
                color: '#666',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                letterSpacing: '0.5px',
                fontWeight: 400
              }}>
                {displayItems.length > 0
                  ? `Showing ${displayItems.length} results for "${searchQuery}"`
                  : `No results found for "${searchQuery}"`
                }
              </p>
              {displayItems.length === 0 && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('Profiles');
                    // Clear URL parameter
                    const newUrl = new URL(window.location);
                    newUrl.searchParams.delete('q');
                    navigate(newUrl.pathname + newUrl.search, { replace: true });
                  }}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: '#0d47a1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Clear Search
                </button>
              )}
            </div>
          )}

          {/* Digital Card Filter (invisible logic component) */}
         {viewMode === 'curated' && (
           <DigitalCardFilter 
             content={allItems}
             onFilteredContent={handleFilteredContentChange}
             filterMode={viewMode}
           />
         )}
         
         {viewMode === 'curated' && (!user || !user.curationSignature) && (
           <div style={{
             padding: '1.5rem',
             background: '#f8f9fa',
             borderRadius: '12px',
             textAlign: 'center',
             marginBottom: '2rem',
             width: '100%'
           }}>
             <p>To see curated content, set up your Digital Card preferences in Settings.</p>
             <Link to="/settings/digital-card" style={{
               display: 'inline-block',
               marginTop: '1rem',
               padding: '0.75rem 1.5rem',
               borderRadius: '6px',
               background: '#0d47a1',
               color: 'white',
               fontWeight: '500',
               textDecoration: 'none'
             }}>
               Set Up Digital Card
             </Link>
           </div>
         )}
         
         {viewMode === 'saved' && (!savedItems || savedItems.length === 0) && (
           <div style={{
             padding: '1.5rem',
             background: '#f8f9fa',
             borderRadius: '12px',
             textAlign: 'center',
             marginBottom: '2rem',
             width: '100%'
           }}>
             <p>You haven't saved any items yet. Browse the Discover feed and click the heart icon to save items.</p>
             <button onClick={() => handleViewModeChange('global')} style={{
               display: 'inline-block',
               marginTop: '1rem',
               padding: '0.75rem 1.5rem',
               borderRadius: '6px',
               background: '#0d47a1',
               color: 'white',
               fontWeight: '500',
               border: 'none',
               cursor: 'pointer'
             }}>
               Browse All Content
             </button>
           </div>
         )}
         
         {visibleItems.length === 0 && !(
           (viewMode === 'curated' && (!user || !user.curationSignature)) ||
           (viewMode === 'saved' && (!savedItems || savedItems.length === 0))
         ) && !isLoading && !searchQuery && (
           <div style={{
             padding: '1.5rem',
             background: '#f8f9fa',
             borderRadius: '12px',
             textAlign: 'center',
             marginBottom: '2rem',
             width: '100%'
           }}>
             <p>No items found for the current view.</p>
             <button onClick={() => handleViewModeChange('global')} style={{
               display: 'inline-block',
               marginTop: '1rem',
               padding: '0.75rem 1.5rem',
               borderRadius: '6px',
               background: '#0d47a1',
               color: 'white',
               fontWeight: '500',
               border: 'none',
               cursor: 'pointer'
             }}>
               View All Content
             </button>
           </div>
         )}
         
         {/* Grid Layout */}
         <div className="discover-grid">
           {visibleItems.map((item, index) => (
             <div 
               key={`${item.id}-${index}`} 
               className="discover-card fade-in"
               onMouseEnter={() => handleCardMouseEnter(item.id)}
               onMouseLeave={() => handleCardMouseLeave(item.id)}
               ref={index === visibleItems.length - 1 ? lastItemRef : null}
               style={{
                 position: 'relative',
                 overflow: 'hidden',
                 borderRadius: '12px',
                 backgroundColor: 'transparent',
                 boxShadow: 'none',
                 transition: 'transform 0.3s ease',
                 margin: 0,
                 padding: 0
               }}
             >
               <Link to={`/discover/${item.id}`} className="card-link">
                 {/* CRITICAL FIX: Images fill full height with mobile optimization */}
                 <div className="card-image-container" style={{
                   width: '100%',
                   position: 'relative',
                   overflow: 'hidden'
                 }}>
                   {/* Only show image if it hasn't failed to load */}
                   {!failedImages[item.id] ? (
                     <img
                       src={item.image}
                       alt={item.title || 'Discover content'}
                       className="card-image"
                       loading="lazy"
                       style={{
                         width: '100%',
                         height: '100%',
                         objectFit: 'cover',
                         objectPosition: 'center',
                         border: 'none',
                         margin: 0,
                         padding: 0,
                         display: 'block'
                       }}
                       onError={() => handleImageError(item.id, item.image, item.imageUrl)}
                       onLoad={(event) => {
                         // Ensure image is visible on mobile
                         if (window.innerWidth < 1300) {
                           const img = event.target;
                           img.style.opacity = '1';
                           img.style.transition = 'opacity 0.3s ease';
                         }
                       }}
                     />
                   ) : (
                     <div className="card-image-fallback" style={{
                       width: '100%',
                       backgroundColor: '#f5f5f5',
                       display: 'flex',
                       flexDirection: 'column',
                       alignItems: 'center',
                       justifyContent: 'center',
                       color: '#666'
                     }}>
                       <div style={{ fontSize: '48px', marginBottom: '1rem' }}>🖼️</div>
                       <div style={{ fontSize: '14px', textAlign: 'center', padding: '0 1rem' }}>
                         {item.title || 'Content'}<br />
                         <small>Image unavailable</small>
                       </div>
                     </div>
                   )}
                 </div>
                 
                 {/* Action buttons - transparent and only visible on hover */}
                 <div
                   style={{
                     position: 'absolute',
                     top: '10px',
                     right: '10px',
                     display: 'flex',
                     flexDirection: 'column',
                     gap: windowWidth < 430 ? '6px' : '8px',
                     opacity: hoverStates[item.id] ? 1 : 0,
                     transition: 'opacity 0.2s ease'
                   }}
                 >
                   <button
                     onClick={(e) => handleSave(e, item)}
                     style={{
                       width: windowWidth < 430 ? '20px' : windowWidth < 768 ? '24px' : '28px',
                       height: windowWidth < 430 ? '20px' : windowWidth < 768 ? '24px' : '28px',
                       borderRadius: '50%',
                       background: 'rgba(255, 255, 255, 0.8)',
                       border: 'none',
                       display: 'flex',
                       justifyContent: 'center',
                       alignItems: 'center',
                       cursor: 'pointer',
                       boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                     }}
                     title="Save"
                   >
                     <FontAwesomeIcon
                       icon={faBookmark}
                       style={{
                         color: isItemSaved(item.id) ? '#000080' : '#666',
                         fontSize: windowWidth < 430 ? '10px' : windowWidth < 768 ? '12px' : '14px'
                       }}
                     />
                   </button>

                   <button
                     onClick={(e) => handleShare(e, item.id)}
                     style={{
                       width: windowWidth < 430 ? '20px' : windowWidth < 768 ? '24px' : '28px',
                       height: windowWidth < 430 ? '20px' : windowWidth < 768 ? '24px' : '28px',
                       borderRadius: '50%',
                       background: 'rgba(255, 255, 255, 0.8)',
                       border: 'none',
                       display: 'flex',
                       justifyContent: 'center',
                       alignItems: 'center',
                       cursor: 'pointer',
                       boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                     }}
                     title="Share"
                   >
                     <FontAwesomeIcon
                       icon={faPaperPlane}
                       style={{
                         color: '#666',
                         fontSize: windowWidth < 430 ? '10px' : windowWidth < 768 ? '12px' : '14px'
                       }}
                     />
                   </button>
                 </div>
                 
                 {/* Hover overlay with info - disabled on mobile */}
                 {windowWidth >= 768 && (
                 <div
                   style={{
                     position: 'absolute',
                     bottom: 0,
                     left: 0,
                     right: 0,
                     background: 'linear-gradient(to top, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.8) 75%, rgba(255, 255, 255, 0.65) 80%, rgba(255, 255, 255, 0.5) 85%, rgba(255, 255, 255, 0.3) 90%, rgba(255, 255, 255, 0.15) 95%, rgba(255, 255, 255, 0) 100%)',
                     padding: '0.8rem 0.8rem 0.6rem 0.8rem',
                     opacity: hoverStates[item.id] ? 1 : 0,
                     transform: hoverStates[item.id] ? 'translateY(0)' : 'translateY(100%)',
                     transition: 'all 0.3s ease',
                     height: '200px',
                     overflow: 'hidden',
                     zIndex: 10,
                     display: 'flex',
                     flexDirection: 'column'
                   }}
                 >
                   <div style={{ 
                     flex: 1,
                     display: 'flex', 
                     flexDirection: 'column',
                     paddingTop: '2.4rem',
                     paddingBottom: '0.3rem'
                   }}>
                     <h3 className="card-title" style={{
                       margin: '0 0 0.4rem 0',
                       fontSize: '0.65rem',
                       fontWeight: 600,
                       color: '#000',
                       display: '-webkit-box',
                       WebkitLineClamp: 2,
                       WebkitBoxOrient: 'vertical',
                       overflow: 'hidden',
                       lineHeight: '1.4',
                       wordBreak: 'break-word'
                     }}>
                       {item.title || 'Untitled'}
                     </h3>

                     {item.description && (
                       <p className="card-description" style={{
                         fontSize: '0.6rem',
                         color: '#666',
                         margin: '0 0 0.5rem 0',
                         lineHeight: '1.4',
                         wordBreak: 'break-word',
                         fontStyle: 'italic',
                         display: '-webkit-box',
                         WebkitLineClamp: 4,
                         WebkitBoxOrient: 'vertical',
                         overflow: 'hidden'
                       }}>
                         {item.description}
                       </p>
                     )}

                     {/* Action buttons */}
                     <div style={{
                       display: 'flex',
                       gap: '0.5rem',
                       marginTop: 'auto'
                     }}>
                       {item.shopProductIds && item.shopProductIds.length > 0 && (
                         <button
                           onClick={(e) => {
                             e.preventDefault();
                             e.stopPropagation();
                             window.location.href = `/shop?from=discover&id=${item.id}`;
                           }}
                           style={{
                             padding: '4px 8px',
                             fontSize: '0.55rem',
                             background: '#0d47a1',
                             color: 'white',
                             border: 'none',
                             borderRadius: '3px',
                             cursor: 'pointer',
                             fontWeight: 600,
                             letterSpacing: '0.5px'
                           }}
                         >
                           SHOP
                         </button>
                       )}
                       {item.source && (
                         <button
                           onClick={(e) => {
                             e.preventDefault();
                             e.stopPropagation();
                             const sourceQuery = encodeURIComponent(item.source);
                             window.location.href = `/discover?source=${sourceQuery}`;
                           }}
                           style={{
                             padding: '4px 8px',
                             fontSize: '0.55rem',
                             background: 'white',
                             color: '#0d47a1',
                             border: '1px solid #0d47a1',
                             borderRadius: '3px',
                             cursor: 'pointer',
                             fontFamily: 'Georgia, serif',
                             fontStyle: 'italic',
                             fontWeight: 500,
                             letterSpacing: '0.3px'
                           }}
                         >
                           {item.source}
                         </button>
                       )}
                     </div>
                   </div>

                                    </div>
                )}
               </Link>
             </div>
           ))}
         </div>
        
        {/* Digital Card reminder */}
        {viewMode === 'global' && user && !user.curationSignature && (
          <div style={{
            padding: '1.5rem',
            background: '#f8f9fa',
            borderRadius: '12px',
            textAlign: 'center',
            marginTop: '2rem',
            width: '100%'
          }}>
            <h3 style={{marginTop: 0}}>Personalize Your Feed</h3>
            <p>Set up your Digital Card to see content tailored to your aesthetic preferences.</p>
            <Link to="/settings/digital-card" style={{
              display: 'inline-block',
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              background: '#0d47a1',
              color: 'white',
              fontWeight: '500',
              textDecoration: 'none'
            }}>
              Create Digital Card
            </Link>
          </div>
        )}
      </div>
      
      {/* Category Filter Bar - Only visible when scrolling up, top layer */}
      {windowWidth < 1300 && (
        <div className={`discover-mobile-category-bar-fixed ${scrollDirection === 'up' ? 'visible' : 'hidden'}`}>
          {categoryOptions.map((category) => (
            <button
              key={category}
              onClick={() => handleCategorySelect(category)}
              className={selectedCategory === category ? 'active' : ''}
            >
              {category}
            </button>
          ))}
        </div>
      )}
      
      {/* Right sidebar with video background - SHOP STYLE */}
      {windowWidth >= 1300 && (
        <>
          {/* Right Sidebar Video Background */}
          <div className="discover-right-video-strip">
            <video
              ref={rightVideoRef}
              src="/videos/kohala-coast-arial.mov"
              muted
              loop={false}
              playsInline
              preload="auto"
              autoPlay
              onLoadedData={() => {
                if (rightVideoRef.current) {
                  setupBoomerangEffect(rightVideoRef.current);
                }
              }}
              onError={(e) => {
                console.error('Right video error:', e);
              }}
            />
          </div>

          {/* Right Sidebar Content */}
          <div className="discover-right-sidebar">
            {/* Suggested accounts section */}
            <div className="sidebar-section" style={{
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              padding: '1rem 1rem 1rem',
              marginLeft: '10px',
              marginRight: '10px',
              marginTop: '10px',
              marginBottom: '20px',
              minHeight: '480px'
            }}>
              <div className="section-header" style={{
                display: 'flex',
                flexDirection: 'column',   
                alignItems: 'flex-start'    
              }}>
                <h3>
                  Suggested Profiles
                </h3>
                <Link to="/profiles" className="see-all-link">
                  See All
                </Link>
              </div>
              
              <div style={{
                display: 'flex', 
                flexDirection: 'column', 
                gap: '0.5rem',
                maxHeight: '300px',
                overflowY: 'auto',
                marginTop: 'rem',
                marginBottom: '1rem'
              }}>
                {suggestedAccounts.map(account => (
                  <div 
                    key={account.id} 
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.25rem 0',
                    }}
                  >
                    <Link to={`/discover/${account.id}`} style={{
                      flexShrink: 0,
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      overflow: 'hidden'
                    }}>
                      {!failedImages[account.id] ? (
                        <img 
                          src={account.image} 
                          alt={account.title || account.source || 'Suggested'} 
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={() => handleImageError(account.id, account.image, account.imageUrl)}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          backgroundColor: '#f5f5f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px'
                        }}>
                          🖼️
                        </div>
                      )}
                    </Link>
                    <div style={{flexGrow: 1, overflow: 'hidden'}}>
                      <Link to={`/discover/${account.id}`} style={{
                        display: 'block',
                        textDecoration: 'none',
                        color: '#111',
                        fontWeight: 600,
                        fontSize: '11px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}>
                        {account.title || 'Untitled'}
                      </Link>
                      <span style={{
                        display: 'block',
                        fontSize: '10px',
                        color: '#666',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {account.source || 'Unknown source'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{
                fontSize: '11px',
                color: '#666',
                fontStyle: 'italic',
                textAlign: 'center',
                padding: '0.5rem',
                marginTop: '0.5rem'
              }}>
                Scroll for more
              </div>
            </div>
            
            {/* Footer */}
            <div className="sidebar-footer" style={{
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              padding: '1rem',
              marginLeft: '10px',
              marginRight: '10px',
              marginBottom: '20px'
            }}>
              <div className="footer-links" style={{
                transform: 'translateX(20px)'
              }}>
                <span>About</span>
                <span>Help</span>
                <span>Terms</span>
                <span>Privacy</span>
              </div>
              <div className="copyright" style={{
                transform: 'translateX(15px)',
                color: '#666'
              }}>
                © Artifex {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  </>
);
};

export default DiscoverWithFilter;